import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { head } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { FileService, PermissionService } from '@ipg/ngx-frontend-toolset';
import { Group } from '../../models/group.model';
import { MediaContext } from '../../models/media-context.model';
import { permissionLabel } from '../../models/permission.model';
import {
  MediaItem,
  MediaResponse,
  SharedMediaResponse,
} from '../../models/media.model';
import { GroupsService } from '../../services/groups.service';
import { MediaService } from '../../services/media.service';
import { PictogramItem, PictogramService } from '../../services/pictogram.service';
import { ShareService } from '../../services/share.service';
import {
  MediaFileCardComponent,
  MediaSelectionChange,
} from '../cards/media-file-card/media-file-card.component';
import { BaseDialog } from '../dialog/base-dialog';
import { ConfirmHandler } from '../dialog/dialog.model';
import { MediaPickerDialogData, MediaPickerType } from './media-picker-dialog.model';

@Component({
  selector: 'app-media-picker-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
    MatIconButton,
    MatProgressSpinner,
    MediaFileCardComponent,
    MatFormField,
    MatInput,
    MatSuffix,
    MatPrefix,
    FormsModule,
  ],
  templateUrl: './media-picker-dialog.component.html',
  styleUrl: './media-picker-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaPickerDialogComponent extends BaseDialog<
  MediaPickerDialogData & {
    onConfirm: ConfirmHandler;
  }
> {
  private readonly _dialogRef =
    inject<MatDialogRef<MediaPickerDialogComponent>>(MatDialogRef);

  private readonly _file = inject(FileService);
  private readonly _groups = inject(GroupsService);
  private readonly _media = inject(MediaService);
  private readonly _pictograms = inject(PictogramService);
  private readonly _share = inject(ShareService);
  private readonly _permissionService = inject(PermissionService);

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged()
  );

  protected readonly keyword = signal<string | null>(null);
  protected readonly isSearching = signal(false);

  private readonly _ctx: MediaContext = this.data.context ?? { scope: 'unit' };
  protected readonly isClientScope = this._ctx.scope === 'client';
  protected readonly unitOnly = this.data.unitOnly === true;
  private readonly _targetUnitId = this.data.targetUnitId ?? null;

  protected readonly canReadMedia = computed(() =>
    this._permissionService.hasPermission('media:read')
  );

  protected readonly canShareMedia = computed(() =>
    this._permissionService.hasPermission('media:share')
  );

  /*
    Each source asks for its own permission, and one the user lacks is never
    fetched: unit and client media need `media:read`, the unit's folders
    `group:read` on top (without it the media show unfiled), and picking a
    system pictogram copies it into the media library, so that tab needs
    `media:create`. Read once: a dialog only opens on a loaded page.
  */
  private readonly _canReadGroups = this._permissionService.hasPermission('group:read');
  protected readonly mediaReadLabel = permissionLabel('media:read');
  private readonly _canCreateMedia = this._permissionService.hasPermission('media:create');

  protected readonly showSystemTab = computed(
    () => !this.unitOnly && this.data.type.includes('image') && this._canCreateMedia
  );

  private readonly _unitCtx: MediaContext = { scope: 'unit' };

  protected readonly viewMode = signal<'client' | 'unit' | 'shared' | 'system'>(
    this.data.initialViewMode ?? (this.isClientScope ? 'client' : 'unit')
  );

  private readonly _unitGroupList = toSignal(
    this.canReadMedia() && this._canReadGroups
      ? this._groups.getList(
          this._unitCtx,
          this._targetUnitId ? { unitId: this._targetUnitId } : undefined,
        )
      : of([]),
    {
      initialValue: [],
    },
  );
  private readonly _clientGroupList = toSignal(
    this.isClientScope && this.canReadMedia() ? this._groups.getList(this._ctx) : of([]),
    { initialValue: [] }
  );

  protected readonly stack = signal<Group[]>([]);

  protected readonly selectedNode = computed(() => this.stack().at(-1) ?? null);

  protected readonly selectedNodeId = computed(() => this.selectedNode()?._id ?? null);

  private readonly _sharedGroupList = toSignal(
    combineLatest([toObservable(this.viewMode), toObservable(this.selectedNodeId)]).pipe(
      switchMap(([mode, nodeId]) => {
        if (mode !== 'shared') return of([]);
        const params: Record<string, string> = {};
        if (nodeId) params['groupId'] = nodeId;
        return this._share.getSharedGroups({ params }).pipe(catchError(() => of([])));
      }),
    ),
    { initialValue: [] },
  );

  protected readonly groups = computed(() => {
    const mode = this.viewMode();
    const keyword = this.keyword();

    if (mode === 'shared') {
      const list = this._sharedGroupList();
      if (keyword) {
        return list.filter((value) =>
          value.label.toLowerCase().includes(keyword.toLowerCase())
        );
      }
      return list;
    }

    const list = mode === 'client' ? this._clientGroupList() : this._unitGroupList();
    return list.filter((value) => value.parentId === this.selectedNodeId());
  });

  protected readonly showFolders = computed(
    () => this.viewMode() !== 'system' && (this.viewMode() === 'shared' || !this.isSearching())
  );

  protected readonly _media$ = combineLatest({
    parentId: toObservable(this.selectedNodeId),
    viewMode: toObservable(this.viewMode),
    keyword: this._debouncedKeyword$.pipe(
      startWith(this.keyword()),
      tap((keyword) => this.isSearching.set(keyword != null && keyword.length > 0))
    ),
  }).pipe(
    switchMap(
      ({
        parentId,
        viewMode,
        keyword,
      }: {
        parentId: string | null;
        viewMode: 'client' | 'unit' | 'shared' | 'system';
        keyword: string | null;
      }) => {
        if (viewMode === 'system') {
          const params: Record<string, string> = {
            limit: '100',
            sortBy: 'label',
          };
          if (keyword) {
            params['q'] = keyword.trim();
          }

          return this._pictograms.getAll({ params }).pipe(
            map((response) =>
              response.items.map((value) => this.mapToSystemMediaItem(value))
            ),
            catchError(() => of([]))
          );
        }

        if (viewMode === 'shared') {
          const sharedParams: { groupId?: string; q?: string } = keyword
            ? { q: keyword.trim() }
            : { groupId: parentId ?? '' };

          return this._share.getSharedMedia({ params: sharedParams }).pipe(
            map((response) =>
              response.map((value) => this.mapToSharedMediaItem(value))
            ),
            map((response) => this.filterMediaItemsByType(response, this.data.type)),
            catchError(() => of([]))
          );
        }

        if (!this.canReadMedia()) {
          return of([]);
        }

        const ctx = viewMode === 'client' ? this._ctx : this._unitCtx;
        const params: { q?: string; parentId?: string; unitId?: string } = keyword
          ? { q: keyword.trim() }
          : { parentId: parentId ?? '' };

        if (this._targetUnitId && viewMode === 'unit') {
          params['unitId'] = this._targetUnitId;
        }

        return this._media.getAll({ params }, ctx).pipe(
          map((response) => response.map((value) => this.mapToMediaItem(value))),
          map((response) => this.filterMediaItemsByType(response, this.data.type)),
          catchError(() => of([]))
        );
      }
    ),
    startWith([])
  );

  // private readonly _media$ = toObservable(this.selectedNodeId).pipe(
  //   map((parentId) => ({
  //     parentId: parentId ?? '',
  //   })),
  //   switchMap((params) =>
  //     this._media
  //       .getAll({
  //         params,
  //       })
  //       .pipe(
  //         map((response) => response.map((value) => this.mapToMediaItem(value))),
  //         map((response) => this.filterMediaItemsByType(response, this.data.type)),
  //         catchError(() => of([]))
  //       )
  //   )
  // );

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected readonly dataSource = toSignal(this._media$, { initialValue: [] });

  protected readonly selection = new SelectionModel<MediaItem>(this.data.multiple, []);

  protected readonly maxSelectionLimit = signal<number | undefined>(undefined);
  protected lastSelectedIndex = -1;

  constructor() {
    super();

    const { maxSelectionLimit, initialSelectionCount = 0 } = this.data;

    const currentLimit =
      maxSelectionLimit != null
        ? Math.max(0, maxSelectionLimit - initialSelectionCount)
        : undefined;

    this.maxSelectionLimit.set(currentLimit);
  }

  isMediaItemDisabled(item: MediaItem) {
    const isSelected = this.selection.isSelected(item);
    const maxSelectionLimit = this.maxSelectionLimit();

    const limitReached =
      maxSelectionLimit != null && this.selection.selected.length >= maxSelectionLimit;

    return limitReached && !isSelected;
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim());
    this._keywordInput$.next(value.trim());
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
  }

  override handleConfirm(): void {
    this.submitted.set(true);
    this.loading.set(true);

    const selection = this.selection.selected;
    const systemItems = selection.filter((item) => item.scope === 'system');

    let prepareSelection$: Observable<MediaItem[]>;

    if (systemItems.length > 0) {
      const targetGroupId = this.data.targetGroupId ?? this.selectedNodeId();
      const targetUnitId = this.isClientScope ? null : (this._targetUnitId ?? null);
      const targetClientId =
        this._ctx.scope === 'client' ? this._ctx.clientId : null;

      prepareSelection$ = this._pictograms
        .copy({
          pictogramIds: systemItems.map((item) => item.id),
          clientId: targetClientId,
          unitId: targetUnitId,
          groupId: targetGroupId,
        })
        .pipe(
          map((createdList) => {
            const createdMediaItems = createdList.map((media) => this.mapToMediaItem(media));
            const mapBySource = new Map(
              createdList.map((m, i) => [m.copiedFrom ?? systemItems[i]?.id, createdMediaItems[i]])
            );
            return selection.map((item) => {
              if (item.scope === 'system') {
                return mapBySource.get(item.id) ?? createdMediaItems[0];
              }
              return item;
            });
          })
        );
    } else {
      prepareSelection$ = of(selection);
    }

    const callback$ = prepareSelection$.pipe(
      switchMap((readySelection: MediaItem[]) =>
        this.data.onConfirm(
          this.data.multiple ? readySelection : head(readySelection)
        )
      )
    );

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);
        this.loading.set(false);

        return throwError(() => error);
      })
    );

    combineLatest([source$, timer(300)])
      .pipe(
        take(1),
        tap(() => this._dialogRef.close(true))
      )
      .subscribe();
  }

  handleSelectionChange({ source, value }: MediaSelectionChange, index: number) {
    const isCtrlPressed = source.ctrlKey;
    const isShiftPressed = source.shiftKey;
    const isSelected = this.selection.isSelected(value);

    if (!this.data.multiple) {
      if (isSelected && this.selection.selected.length === 1) {
        this.selection.clear();
        this.lastSelectedIndex = -1;

        return;
      }
      this.selection.clear();
      this.selection.select(value);
      this.lastSelectedIndex = index;

      return;
    }

    if (isShiftPressed && this.lastSelectedIndex > -1) {
      const minIndex = Math.min(this.lastSelectedIndex, index);
      const maxIndex = Math.max(this.lastSelectedIndex, index);
      const range = this.dataSource().slice(minIndex, maxIndex + 1);
      const selectionLimit = this.maxSelectionLimit();

      const unselectedRange = range.filter(
        (value) => !this.selection.isSelected(value)
      );

      let itemsToSelect = unselectedRange;

      if (selectionLimit) {
        const currentSelected = this.selection.selected.length;
        const remaining = Math.max(0, selectionLimit - currentSelected);

        if (remaining <= 0) {
          return;
        }

        if (index > this.lastSelectedIndex) {
          itemsToSelect = unselectedRange.slice(0, remaining);
        } else {
          itemsToSelect = unselectedRange.slice(-remaining);
        }
      }

      this.selection.select(...itemsToSelect);
      this.lastSelectedIndex = index;
    } else if (isCtrlPressed) {
      this.selection.toggle(value);
      this.lastSelectedIndex = index;
    } else {
      if (isSelected && this.selection.selected.length === 1) {
        this.selection.clear();
        this.lastSelectedIndex = -1;

        return;
      }
      this.selection.clear();
      this.selection.select(value);
      this.lastSelectedIndex = index;
    }
  }

  navigateNext(node: Group) {
    this.stack.update((currentState) => [...currentState, node]);

    this.selection.clear();
  }

  navigatePrevious() {
    this.stack.update((currentState) => currentState.slice(0, -1));

    this.selection.clear();
  }

  switchView(mode: 'client' | 'unit' | 'shared' | 'system') {
    this.viewMode.set(mode);
    this.stack.set([]);
    this.selection.clear();
  }

  getTypeFromMimeType(mimeType: string) {
    const parts = mimeType.split('/');

    return parts.length > 1 ? parts[0] : null;
  }

  getMediaFileIcon(mimeType?: string) {
    if (!mimeType) {
      return 'draft';
    }

    const type = this.getTypeFromMimeType(mimeType);

    switch (type) {
      case 'audio':
        return 'music_note';

      case 'image':
        return 'image';

      case 'video':
        return 'movie';

      default:
        return 'draft';
    }
  }

  mapToMediaItem(value: MediaResponse): MediaItem {
    const mediaIcon = this.getMediaFileIcon(value.metadata.mimeType);
    const type = this.getTypeFromMimeType(value.metadata.mimeType);
    const scope = (this.viewMode() === 'system'
      ? (this.isClientScope ? 'client' : 'unit')
      : this.viewMode()) as 'unit' | 'client' | 'shared';

    return {
      id: value._id,
      label: value.label,
      icon: mediaIcon,
      fileId: value.fileId,
      fileUrl: this._file.get(value.fileId).href,
      type,
      shareId: value.shareId,
      scope,
      duration: 'duration' in value.metadata ? value.metadata.duration : undefined,
    };
  }

  mapToSystemMediaItem(value: PictogramItem): MediaItem {
    return {
      id: value._id,
      label: value.label,
      icon: 'category',
      fileId: value.fileId,
      fileUrl: this._pictograms.getFileUrl(value.fileId),
      type: 'image',
      shareId: null,
      scope: 'system',
    };
  }

  mapToSharedMediaItem(value: SharedMediaResponse): MediaItem {
    return {
      ...this.mapToMediaItem(value),
      scope: 'shared',
    };
  }

  filterMediaItemsByType(value: MediaItem[], type: MediaPickerType[]) {
    return value.filter((item) =>
      item.type ? type.includes(item.type as MediaPickerType) : true
    );
  }
}
